import React, { useState, useRef } from 'react';

/**
 * Stepped Slider - Discrete slider with snap points.
 *
 * Hybrid of VAS (slider interaction) and NRS (discrete values).
 * The slider snaps to predefined positions, each with an optional label.
 * Useful for ordinal scales where a slider metaphor is more intuitive
 * than buttons but continuous values aren't appropriate.
 *
 * Usage: responseConfig.layout = 'stepped-slider'
 */
const SteppedSlider = ({
  options = [],
  value = null,
  onChange,
  disabled = false,
  trackColor = '#e2e8f0',
  activeColor = '#0d9488',
  showLabels = true,
  showValues = false,
  height = 48,
  thumbSize = 24,
  fontSize = 12,
  className = '',
}) => {
  const steps = options.length > 0
    ? options.map((opt, i) => ({
        label: typeof opt === 'object' ? opt.label : opt,
        value: typeof opt === 'object' ? opt.value : String(i),
      }))
    : [{ label: 'Min', value: '0' }, { label: 'Max', value: '1' }];

  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (value === null) return null;
    const idx = steps.findIndex(s => String(s.value) === String(value));
    return idx >= 0 ? idx : null;
  });

  const trackRef = useRef(null);

  const handleTrackClick = (e) => {
    if (disabled || steps.length < 2) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const idx = Math.round(percent * (steps.length - 1));
    const clamped = Math.max(0, Math.min(steps.length - 1, idx));
    setSelectedIndex(clamped);
    if (onChange) onChange(steps[clamped].value);
  };

  const handleStepClick = (idx) => {
    if (disabled) return;
    setSelectedIndex(idx);
    if (onChange) onChange(steps[idx].value);
  };

  const thumbPercent = selectedIndex !== null ? (selectedIndex / Math.max(steps.length - 1, 1)) * 100 : 0;
  const fillPercent = selectedIndex !== null ? thumbPercent : 0;

  return (
    <div className={className} style={{ width: '100%', padding: '8px 0' }}>
      {/* Slider track */}
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        style={{
          position: 'relative',
          height,
          display: 'flex',
          alignItems: 'center',
          cursor: disabled ? 'default' : 'pointer',
          padding: `0 ${thumbSize / 2}px`,
        }}
      >
        {/* Track background */}
        <div style={{
          position: 'absolute',
          left: thumbSize / 2,
          right: thumbSize / 2,
          height: 6,
          backgroundColor: trackColor,
          borderRadius: 3,
        }} />

        {/* Active fill */}
        {selectedIndex !== null && (
          <div style={{
            position: 'absolute',
            left: thumbSize / 2,
            width: `calc(${fillPercent}% - ${thumbSize / 2}px)`,
            height: 6,
            backgroundColor: activeColor,
            borderRadius: 3,
            transition: 'width 0.15s ease',
          }} />
        )}

        {/* Step dots */}
        {steps.map((step, i) => {
          const percent = (i / Math.max(steps.length - 1, 1)) * 100;
          const isActive = selectedIndex !== null && i <= selectedIndex;
          const isSelected = i === selectedIndex;

          return (
            <div
              key={i}
              onClick={(e) => { e.stopPropagation(); handleStepClick(i); }}
              style={{
                position: 'absolute',
                left: `calc(${percent}% )`,
                transform: 'translateX(-50%)',
                width: isSelected ? thumbSize : 12,
                height: isSelected ? thumbSize : 12,
                borderRadius: '50%',
                backgroundColor: isActive ? activeColor : '#fff',
                border: `2px solid ${isActive ? activeColor : '#94a3b8'}`,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
                zIndex: isSelected ? 2 : 1,
                boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
              }}
            />
          );
        })}
      </div>

      {/* Labels */}
      {showLabels && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: `4px ${thumbSize / 2}px 0`,
        }}>
          {steps.map((step, i) => (
            <span
              key={i}
              onClick={() => handleStepClick(i)}
              style={{
                fontSize,
                color: i === selectedIndex ? '#1e293b' : '#94a3b8',
                fontWeight: i === selectedIndex ? '600' : '400',
                fontFamily: 'system-ui, sans-serif',
                textAlign: 'center',
                cursor: disabled ? 'default' : 'pointer',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {showValues ? step.value : step.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SteppedSlider;
